import { Point } from 'geojson';
import { isObject } from 'lodash';

export class SnLonLat {
  private _lat: number = 0;
  private _lon: number = 0;

  public static convert(lonLatLike: SnLonLatLike): SnLonLat {
    if (lonLatLike instanceof SnLonLat) {
      return lonLatLike;
    }

    if (Array.isArray(lonLatLike) && lonLatLike.length >= 2) {
      return new SnLonLat(lonLatLike[0], lonLatLike[1]);
    }

    if (!Array.isArray(lonLatLike) && isObject(lonLatLike)) {
      return new SnLonLat('lon' in lonLatLike ? lonLatLike.lon : lonLatLike.lng, lonLatLike.lat);
    }

    console.error(`
      SnLonLatLike argument must be specified as a SnLonLat instance,
      an object {lon: <lon>, lat: <lat>}, an object {lng: <lon>, lat: <lat>},
      or an array of [<lon>, <lat>]
    `);
  }

  public static fromGeojsonPoint(point: Point): SnLonLat {
    const [lon, lat] = point.coordinates;

    return new SnLonLat(lon, lat);
  }

  constructor(lon: number, lat: number) {
    if (isNaN(lon) || isNaN(lat)) {
      console.error(`Invalid SnLonLat object: (${lon}, ${lat})`);
      return;
    }

    this.lon = lon;
    this.lat = lat;
  }

  get lat(): number {
    return this._lat;
  }
  set lat(value: number) {
    if (value > 90 || value < -90) {
      console.error('Invalid SnLonLat latitude value: must be between -90 and 90');
      return;
    }

    this._lat = +value;
  }

  get lon(): number {
    return this._lon;
  }
  set lon(value: number) {
    if (value > 180 || value < -180) {
      console.error('Invalid SnLonLat longitude value: must be between -180 and 180');
      return;
    }

    this._lon = +value;
  }

  public clone(): SnLonLat {
    return new SnLonLat(this.lon, this.lat);
  }

  public toArray(): [number, number] {
    return [this.lon, this.lat];
  }

  public toString(): string {
    return `(${this.lat}, ${this.lon})`;
  }

  public toUrlValue(): string {
    return `${this.lat},${this.lon}`;
  }

  public toLatLngLiteral(): google.maps.LatLngLiteral {
    return {
      lat: this.lat,
      lng: this.lon,
    };
  }

  public toJSON(): ISnLonLatLiteral {
    return {
      lat: this.lat,
      lon: this.lon,
    };
  }

  public toGeojsonPoint(): Point {
    return {
      type: 'Point',
      coordinates: this.toArray(),
    };
  }
}

export interface ISnLonLatLiteral {
  lat: number;
  lon: number;
}

export interface ISnLngLatLiteral {
  lat: number;
  lng: number;
}

export type SnLonLatLike =
  | SnLonLat
  | ISnLonLatLiteral
  | { lon: number; lat: number }
  | { lng: number; lat: number }
  | [number, number]
  | number[];
